/**
 * Right panel: variation analysis.
 *
 * Two states:
 *
 * 1. **Empty / ready.** No live determination yet. Show a control
 *    panel that picks the most-recent base contract + variation
 *    submission and offers a "Run determination" button.
 * 2. **Live.** A live VerifiedDetermination is loaded. Render its
 *    fields into the analysis sections: recommended decision card,
 *    variation summary, entitlement finding with cited evidence,
 *    valuation line table, time impact, non-compliance items.
 *
 * The polling cadence backs off from 1s to 8s while the assessment
 * runs (typical end-to-end is 30-90 seconds with Sonnet 4.6 plus
 * Voyage retrieval).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "../icons";
import {
  approveDetermination,
  createAssessment,
  createChatSession,
  deliverDetermination,
  fetchArtifact,
  getAssessment,
  getChatSession,
  getDetermination,
  listContractors,
  listContracts,
  deleteDocument,
  listDocuments,
  loadConfig,
  patchChatSession,
  patchDetermination,
  requestChangesOnDetermination,
  sendForApproval,
  type ApiError,
  type AssessmentRecord,
  type AssessmentStatus,
  type ChatMessage,
  type ContractOut,
  type ContractorOut,
  type DeterminationEdit,
  type DeterminationPayload,
  type DocumentRecord,
  type WorkflowTemplate,
} from "../api/client";
import { useToast } from "../Toasts";
import { BriefingNoteDialog } from "./BriefingNoteDialog";

type Tab = "analysis" | "documents" | "history";
type Phase = "idle" | "queued" | "running" | "complete" | "failed";

const POLL_BASE_MS = 1000;
const POLL_MAX_MS = 8000;
// Abort a single status poll that stalls rather than letting a hung
// socket freeze the indicator on a stale "Running". Polls are cheap
// GETs, so a 12s ceiling is safe and never cuts off a healthy response.
const POLL_REQUEST_TIMEOUT_MS = 12000;
// Final backstop for the whole polling session. 16 min sits just above
// the server's 15-min stuck-assessment reaper, so the server converges
// an orphaned row to FAILED first and the UI catches up; this ceiling
// only fires if even that never arrives.
const POLL_CEILING_MS = 16 * 60 * 1000;
const VARIATION_DOC_TYPES = new Set(["variation_submission", "daywork_sheet"]);
const CONTRACT_DOC_TYPES = new Set([
  "base_contract",
  "standard",
  "rate_table",
  "explainer",
  "drawing",
]);

function isTransientFailure(reason: string | null | undefined): boolean {
  if (!reason) return false;
  const lower = reason.toLowerCase();
  // These shapes all mean "the upstream was slow or busy, not your inputs",
  // so re-running the same variation is the right next action. Kept in sync
  // with the engine's FailureKind taxonomy (ADR-0025): "wall-clock"/
  // "deadline" is DEADLINE_EXCEEDED, "overloaded" is a 529.
  return (
    lower.includes("apitimeout") ||
    lower.includes("apiconnection") ||
    lower.includes("ratelimit") ||
    lower.includes("rate_limit") ||
    lower.includes("timed out") ||
    lower.includes("connection error") ||
    lower.includes("overloaded") ||
    lower.includes("wall-clock") ||
    lower.includes("deadline")
  );
}

function isPlatformFault(reason: string | null | undefined): boolean {
  if (!reason) return false;
  const lower = reason.toLowerCase();
  // The runner's pre-run probe (ADR-0029) names the search index when the
  // vector store is unreachable. Nothing about the operator's inputs caused
  // it and nothing about them will fix it, so the advice must not be
  // "adjust the inputs": that was the misleading message on 2026-11-03.
  return lower.includes("search index") || lower.includes("vector store");
}

function classifyForRun(value: string): "variation" | "contract" | "other" {
  const cleaned = value.trim().toLowerCase().replace(/-/g, "_");
  if (VARIATION_DOC_TYPES.has(cleaned)) return "variation";
  if (CONTRACT_DOC_TYPES.has(cleaned)) return "contract";
  return "other";
}

// Render the operator + assistant turns of a chat session as plain
// text, to carry into a determination run as non-evidence context
// (S16, ADR-0017). System turns are dropped; the engine labels the
// block and still cites only chunks it retrieves itself.
export function buildChatTranscript(messages: ChatMessage[]): string {
  return messages
    .filter((m) => m.role === "user" || m.role === "assistant")
    .map(
      (m) =>
        `${m.role === "assistant" ? "Assistant" : "Operator"}: ${m.content.trim()}`,
    )
    .join("\n\n");
}

export function chatContextLabel(count: number): string {
  return `Assistant discussion, ${count} message${count === 1 ? "" : "s"}`;
}

function statusToPhase(s: AssessmentStatus): Phase {
  if (s === "queued") return "queued";
  if (s === "running") return "running";
  if (s === "complete") return "complete";
  return "failed";
}

function formatMoney(value: string | number | undefined | null): string {
  if (value === null || value === undefined || value === "") return "-";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return String(value);
  if (num === 0) return "A$0";
  const sign = num < 0 ? "-" : "";
  const formatted = Math.abs(num).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${sign}A$${formatted}`;
}

// The agent occasionally submits 0 / "0.00" for these top-line
// figures even when the valuation table carries real numbers.
// When the persisted value is zero but the lines aren't, derive
// the totals from the lines so the Variation Summary matches the
// Cost & Schedule Impact table.
function deriveFinalAssessed(
  det: DeterminationPayload["determination"],
): string {
  const persisted = Number(det.final_assessed_value);
  if (!Number.isNaN(persisted) && persisted !== 0) return det.final_assessed_value;
  if (det.valuation_lines.length === 0) return det.final_assessed_value;
  const sum = det.valuation_lines.reduce(
    (acc, line) => acc + (Number(line.assessed_amount) || 0),
    0,
  );
  return sum.toFixed(2);
}

function deriveAdjustment(
  det: DeterminationPayload["determination"],
): string {
  const persisted = Number(det.contract_sum_adjustment);
  if (!Number.isNaN(persisted) && persisted !== 0) return det.contract_sum_adjustment;
  if (det.valuation_lines.length === 0) return det.contract_sum_adjustment;
  // Adjustment = the net effect on the contract sum, signed. For an
  // addition it equals the sum of assessed amounts; for an omission
  // the assessed amounts are already negative.
  const sum = det.valuation_lines.reduce(
    (acc, line) => acc + (Number(line.assessed_amount) || 0),
    0,
  );
  return sum.toFixed(2);
}

export function VariationPanel({
  onActiveAssessmentChange,
  onActiveDeterminationChange,
  chatLinkedAssessmentId,
  onSessionCreated,
  currentSessionId,
  onAssistantSessionRelinked,
}: {
  onActiveAssessmentChange: (a: AssessmentRecord | null) => void;
  onActiveDeterminationChange: (d: DeterminationPayload | null) => void;
  chatLinkedAssessmentId: string | null;
  // Fired after Run successfully creates a fresh chat session
  // bound to the new assessment. The parent uses this to swap the
  // active session in the assistant panel (so the chat clears and
  // re-opens empty against the new variation) and to refresh the
  // Chats list so the operator can see the new entry.
  onSessionCreated: (sessionId: string) => void;
  // The Assistant's active session id, so "Link to Assistant" can
  // re-point it in place rather than spawning a new chat.
  currentSessionId: string | null;
  // Fired after an in-place relink so the parent can prompt the
  // AssistantPanel to re-read the (same-id) session and refresh the
  // Chats list. See ADR-0015.
  onAssistantSessionRelinked: () => void;
}) {
  const toast = useToast();
  const [tab, setTab] = useState<Tab>("analysis");
  const [docs, setDocs] = useState<DocumentRecord[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [phase, setPhase] = useState<Phase>("idle");
  const [assessment, setAssessment] = useState<AssessmentRecord | null>(null);
  const [determination, setDetermination] = useState<DeterminationPayload | null>(null);
  const [running, setRunning] = useState(false);
  const [linking, setLinking] = useState(false);
  // Run-options sheet (S16). Opened from the run gate when the active
  // chat has content (offer to carry it in, ADR-0017), or from the
  // header when a determination is on file (offer new vs augment,
  // ADR-0018). ``transcript`` is the prepared chat text; ``chatCount``
  // drives the copy and whether the include control is shown.
  const [runSheet, setRunSheet] = useState<
    { kind: "fresh" | "rerun"; chatCount: number; transcript: string } | null
  >(null);
  const [includeChat, setIncludeChat] = useState(true);
  const [augmentChoice, setAugmentChoice] = useState<"new" | "augment">(
    "augment",
  );
  const [variationDocId, setVariationDocId] = useState<string>("");
  // Run-gate state: pick contractor → contract → variation. The
  // engine call is gated behind all three; the dropdowns cascade
  // (changing contractor resets contract, changing contract resets
  // the variation pick) so the operator cannot end up running a
  // variation against the wrong contract.
  const [contractors, setContractors] = useState<ContractorOut[]>([]);
  const [contracts, setContracts] = useState<ContractOut[]>([]);
  const [selectedContractorId, setSelectedContractorId] = useState<string>("");
  const [selectedContractId, setSelectedContractId] = useState<string>("");

  // Forward state up to App so the chrome renders the live
  // breadcrumbs and document title.
  useEffect(() => {
    onActiveAssessmentChange(assessment);
  }, [assessment, onActiveAssessmentChange]);
  useEffect(() => {
    onActiveDeterminationChange(determination);
  }, [determination, onActiveDeterminationChange]);

  // When the operator opens a chat that has an assessment_id linked,
  // pull that assessment + its determination into this panel so they
  // can resume the variation as if they had just run it. A null
  // chat-linked id means the chat has no associated assessment yet
  // (e.g. a brand-new "General chat") - leave the panel as-is.
  //
  // The "I just loaded this chat-linked id" guard sits on a ref, NOT
  // on `assessment?.assessment_id` in the dep array. Earlier the dep
  // array carried assessment.assessment_id and the early-return read
  // it from the closure, but that made the effect re-fire on every
  // local setAssessment - including the one inside onRun. The race
  // was: operator with a prior chat (say Matland V39) clicks Run on
  // Nilsen NV 88, onRun does setAssessment(NV 88), this effect re-
  // fires before onSessionCreated has had time to update the chat-
  // linked id, sees a mismatch, and re-fetches the OLD Matland
  // assessment + determination right over the top of the just-set
  // NV 88 one. Chat (bound to the new session) was unaffected,
  // hence the colour-of-glove symptom: chat right, panel wrong.
  const lastLoadedChatAssessmentRef = useRef<string | null>(null);
  useEffect(() => {
    if (!chatLinkedAssessmentId) {
      lastLoadedChatAssessmentRef.current = null;
      return;
    }
    if (lastLoadedChatAssessmentRef.current === chatLinkedAssessmentId) return;
    lastLoadedChatAssessmentRef.current = chatLinkedAssessmentId;
    let cancelled = false;
    const cfg = loadConfig();
    void (async () => {
      try {
        const fresh = await getAssessment(cfg, chatLinkedAssessmentId);
        if (cancelled) return;
        setAssessment(fresh);
        setPhase(statusToPhase(fresh.status));
        if (fresh.status === "complete") {
          try {
            const det = await getDetermination(cfg, fresh.assessment_id);
            if (!cancelled) setDetermination(det);
          } catch {
            if (!cancelled) setDetermination(null);
          }
        } else {
          setDetermination(null);
        }
      } catch (err) {
        if (cancelled) return;
        const apiErr = err as ApiError;
        if (apiErr.status !== 404) {
          toast.show("err", `Could not load chat's assessment: ${apiErr.message ?? "unknown"}`);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chatLinkedAssessmentId, toast]);

  // Refresh the contractor + contract pickers on mount and on the
  // same cadence as documents below. A new entity added on the
  // Contacts tab (or via the Workspace gating block on a contract
  // upload) shows up here without a manual reload.
  useEffect(() => {
    let cancelled = false;
    const cfg = loadConfig();
    async function refresh() {
      try {
        const [crs, cts] = await Promise.all([
          listContractors(cfg),
          listContracts(cfg),
        ]);
        if (cancelled) return;
        setContractors(crs);
        setContracts(cts);
      } catch {
        // Quietly retry on the next poll; the Contacts surface
        // raises its own errors if the operator tries to manage
        // entities while the service is down.
      }
    }
    void refresh();
    const handle = window.setInterval(() => void refresh(), 4000);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, []);

  // Cascade resets: changing the active contractor clears the
  // contract pick to "" so the second dropdown starts empty;
  // changing the contract clears the variation pick.
  useEffect(() => {
    setSelectedContractId((current) => {
      if (!selectedContractorId) return "";
      const stillValid = contracts.some(
        (c) => c.id === current && c.contractor_id === selectedContractorId,
      );
      return stillValid ? current : "";
    });
  }, [selectedContractorId, contracts]);
  useEffect(() => {
    setVariationDocId("");
  }, [selectedContractId]);

  // Fetch documents on mount and poll every few seconds so a fresh
  // upload from the left panel surfaces in the run-panel dropdown
  // without a manual reload. We deliberately do NOT auto-load any
  // prior assessment - every page session starts in the empty
  // state and the operator explicitly clicks Run determination to
  // produce live output.
  useEffect(() => {
    let cancelled = false;
    const cfg = loadConfig();
    let firstLoad = true;
    const poll = (): void => {
      if (firstLoad) setDocsLoading(true);
      listDocuments(cfg)
        .then((docList) => {
          if (cancelled) return;
          setDocs(docList);
          // No first-load auto-pick anymore: the variation
          // dropdown is filtered by the operator's chosen contract,
          // so picking a variation before picking a contract would
          // populate the field with a row the gate will then
          // immediately reject. The empty default is the right
          // starting state.
        })
        .catch((err: ApiError) => {
          if (firstLoad && !cancelled) {
            toast.show("err", `Could not load documents: ${err.message ?? "unknown"}`);
          }
        })
        .finally(() => {
          if (firstLoad) {
            firstLoad = false;
            if (!cancelled) setDocsLoading(false);
          }
        });
    };
    poll();
    const handle = window.setInterval(poll, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inline document deletion from the "in scope" corpus list so
  // operators can clear stale starter documents (e.g. AS-4000.pdf,
  // AS-4300.pdf) without leaving the Run-determination panel.
  // Confirms first; on success drops the row from local state
  // immediately so the corpus summary reflects the deletion.
  const onDeleteCorpusDocument = useCallback(
    async (doc: DocumentRecord): Promise<void> => {
      const ok = window.confirm(
        `Delete ${doc.original_filename}? Its indexed chunks will be removed too;`
        + " new determinations will not see it.",
      );
      if (!ok) return;
      const cfg = loadConfig();
      try {
        await deleteDocument(cfg, doc.document_id);
        setDocs((prev) => prev.filter((d) => d.document_id !== doc.document_id));
        toast.show("ok", `Deleted ${doc.original_filename}.`);
      } catch (err) {
        const apiErr = err as ApiError;
        toast.show("err", `Delete failed: ${apiErr.message ?? "unknown"}`);
      }
    },
    [toast],
  );

  // Poll the active assessment while it's running.
  useEffect(() => {
    if (!assessment || phase === "complete" || phase === "failed") return;
    let cancelled = false;
    const cfg = loadConfig();
    let delay = POLL_BASE_MS;
    let timer: number | null = null;
    // Wall-clock start of this polling session and a consecutive-error
    // counter. The catch block below uses them to (a) keep polling
    // through a transient hiccup instead of freezing on a stale status,
    // and (b) give up cleanly at the ceiling rather than spinning
    // forever.
    const startedAt = Date.now();
    let consecutiveErrors = 0;
    const tick = async () => {
      // Final backstop: if the run has outlived the server-side reaper
      // window without ever resolving, stop and tell the operator
      // plainly rather than leaving a permanent spinner.
      if (Date.now() - startedAt > POLL_CEILING_MS) {
        if (cancelled) return;
        setPhase("failed");
        toast.show(
          "err",
          "Determination is taking longer than expected and may have failed. Refresh to check.",
        );
        return;
      }
      try {
        const fresh = await getAssessment(
          cfg,
          assessment.assessment_id,
          POLL_REQUEST_TIMEOUT_MS,
        );
        if (cancelled) return;
        consecutiveErrors = 0;
        setAssessment(fresh);
        sessionStorage.setItem("cvp.ui.activeAssessment", JSON.stringify(fresh));
        const next = statusToPhase(fresh.status);
        setPhase(next);
        if (next === "complete") {
          // The dedicated effect below picks up the determination
          // fetch; we just stop polling here.
          return;
        }
        if (next === "failed") {
          toast.show(
            "err",
            `Determination failed: ${fresh.failure_reason ?? "unknown"}`,
          );
          return;
        }
        delay = Math.min(delay * 1.5, POLL_MAX_MS);
        timer = window.setTimeout(() => void tick(), delay);
      } catch (err) {
        if (cancelled) return;
        const apiErr = err as ApiError;
        // Stored assessment id no longer exists (e.g. backend restart);
        // clear it silently and show the empty state again.
        if (apiErr.status === 404) {
          sessionStorage.removeItem("cvp.ui.activeAssessment");
          setAssessment(null);
          setDetermination(null);
          setPhase("idle");
          return;
        }
        // Transient transport error (timeout, proxy 5xx, dropped
        // socket): do NOT stop polling. A single hiccup must not freeze
        // the indicator on the last-known status, so back off and try
        // again. Toast only on the first failure of a streak so the
        // operator is not spammed while the network settles.
        consecutiveErrors += 1;
        if (consecutiveErrors === 1) {
          toast.show(
            "err",
            `Status poll hiccup: ${apiErr.message ?? "unknown"}. Retrying...`,
          );
        }
        delay = Math.min(delay * 1.5, POLL_MAX_MS);
        timer = window.setTimeout(() => void tick(), delay);
      }
    };
    timer = window.setTimeout(() => void tick(), delay);
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [assessment?.assessment_id, phase, toast]);

  // Dedicated effect: fetch the determination when phase flips to
  // complete. Living in its own effect means the polling effect's
  // cleanup (which fires when phase changes) cannot race with the
  // determination fetch and orphan it - the previous all-in-one
  // version dropped the determination on the floor. The fetched-id
  // ref guards against a tight retry loop when the GET 404s: the
  // polling effect re-runs setAssessment every tick (new object
  // reference even when the row is unchanged), and toast updates
  // also re-render this component, so without the ref a single
  // failed fetch would re-trigger this effect indefinitely.
  const fetchedDeterminationRef = useRef<string | null>(null);
  const assessmentId = assessment?.assessment_id ?? null;
  useEffect(() => {
    if (phase !== "complete" || assessmentId === null) return;
    if (fetchedDeterminationRef.current === assessmentId) return;
    fetchedDeterminationRef.current = assessmentId;
    // No cancelled flag: depending only on (phase, assessmentId)
    // means this effect does not re-run on each polling tick that
    // setAssessment fires. Without the flag, the in-flight fetch
    // cannot be marked stale and dropped by a churning cleanup.
    const cfg = loadConfig();
    getDetermination(cfg, assessmentId)
      .then((det) => {
        setDetermination(det);
      })
      .catch((err: ApiError) => {
        toast.show(
          "err",
          `Determination fetch failed: ${err.message ?? "unknown"}`,
        );
      });
  }, [phase, assessmentId, toast]);

  const variationDoc = docs.find((d) => d.document_id === variationDocId);
  // Scope the contract corpus shown here to documents bound to the
  // currently-selected contract. Without this, every contract-typed
  // document in the tenant counts as "contract corpus" and the
  // baseContract fallback (contractCorpus[0]) silently picks up
  // another contract's head-contract PDF, which then drives the
  // determination's preflight identity check and citations against
  // the wrong contract entirely.
  const contractCorpus = useMemo(
    () =>
      docs.filter((d) => {
        if (classifyForRun(d.doc_type ?? "") !== "contract") return false;
        if (!selectedContractId) return false;
        // The base-contract doc itself is bound to the contract via
        // ContractRow.document_id, not via DocumentRow.contract_id;
        // the doc record's contract_id is NULL by design. Match it
        // by id against the selected contract's document_id, and
        // match every other corpus doc by its own contract_id.
        const selected = contracts.find((c) => c.id === selectedContractId);
        if (!selected) return false;
        if (selected.document_id && d.document_id === selected.document_id) {
          return true;
        }
        return d.contract_id === selectedContractId;
      }),
    [docs, contracts, selectedContractId],
  );

  // The actual fresh-run path: validate the gate inputs, create a new
  // assessment, and open a fresh chat (Run is a hard reset of the
  // assistant, ADR-0015). ``additionalContext`` carries the operator's
  // chat discussion in when they opted to include it (ADR-0017).
  const doFreshRun = useCallback(
    async (
      additionalContext: string,
      additionalContextLabel: string,
    ): Promise<void> => {
      const selectedContract = contracts.find(
        (c) => c.id === selectedContractId,
      );
      if (!selectedContractorId) {
        toast.show("err", "Pick a contractor first.");
        return;
      }
      if (!selectedContract) {
        toast.show("err", "Pick a contract first.");
        return;
      }
      if (!variationDoc) {
        toast.show("err", "Pick a variation submission first.");
        return;
      }
      // The base contract document the engine runs the preflight
      // identity check against is the document linked to the chosen
      // contract row. Refuse the run when no link exists - the
      // previous fallback to contractCorpus[0] silently grabbed
      // another contract's head contract PDF, which then drove the
      // determination's identity check, citations and findings
      // against the wrong contract. Better to fail loud and tell the
      // operator to relink the contract on the Contacts tab.
      if (!selectedContract.document_id) {
        toast.show(
          "err",
          `${selectedContract.reference} has no head-contract document linked. Relink it on the Contacts tab or upload a fresh head contract.`,
        );
        return;
      }
      const baseContract = contractCorpus.find(
        (d) => d.document_id === selectedContract.document_id,
      );
      if (!baseContract) {
        toast.show(
          "err",
          `Head contract document for ${selectedContract.reference} is missing from the corpus. Re-upload it on the Contacts tab.`,
        );
        return;
      }
      setRunning(true);
      const cfg = loadConfig();
      try {
        const variationId = `V-${Date.now().toString(36).slice(-6).toUpperCase()}`;
        const created = await createAssessment(cfg, {
          // The contract reference the engine stamps on the
          // assessment is the operator-visible reference of the
          // chosen contract, not a hardcoded constant.
          contract_id: selectedContract.reference,
          variation_id: variationId,
          workflow_template: "variation_assessment",
          base_contract_document_id: baseContract.document_id,
          submission_document_id: variationDoc.document_id,
          contractor_expected: "",
          ...(additionalContext
            ? {
                additional_context: additionalContext,
                additional_context_label: additionalContextLabel,
              }
            : {}),
        });
        setAssessment(created);
        sessionStorage.setItem("cvp.ui.activeAssessment", JSON.stringify(created));
        setPhase(statusToPhase(created.status));
        setDetermination(null);
        // Always open a fresh chat session for the new run. The
        // previous behaviour was to patch the active session in
        // place when one existed, which left the prior conversation
        // visible against the new variation; the operator's
        // expectation is that pressing Run is a hard reset of the
        // assistant. The previous session stays in the Chats list
        // (the operator can return to it any time); the new one is
        // surfaced via the onSessionCreated callback so the parent
        // swaps the active session id and refreshes the list.
        try {
          const linked = await createChatSession(cfg, {
            kind: "workspace",
            title: `${variationId} - ${variationDoc.original_filename}`,
            variation_label: variationId,
            assessment_id: created.assessment_id,
            variation_brief: {
              variation_id: variationId,
              title: `Variation flow for ${variationDoc.original_filename}`,
              // Retrieval scope (ADR-0015): now safe to carry on a
              // workspace chat because the surface is the explicit
              // `kind`, not the presence of a contract_id. Without it,
              // this chat's retrieval was tenant-wide.
              contract_id: selectedContract.id,
              contract_reference: selectedContract.reference,
              documents: [
                {
                  name: variationDoc.original_filename,
                  role: "variation submission",
                  note: `Uploaded ${new Date(variationDoc.received_at).toLocaleString("en-AU")}.`,
                },
              ],
            },
          });
          onSessionCreated(linked.session_id);
        } catch (linkErr) {
          const linkApiErr = linkErr as ApiError;
          toast.show(
            "err",
            `Run started but chat link failed: ${linkApiErr.message ?? "unknown"}`,
          );
        }
        toast.show(
          "ok",
          `Queued against ${contractCorpus.length} contract document${contractCorpus.length === 1 ? "" : "s"}.`,
        );
      } catch (err) {
        const apiErr = err as ApiError;
        toast.show("err", `Run failed: ${apiErr.message ?? "unknown"}`);
      } finally {
        setRunning(false);
      }
    },
    [
      variationDoc,
      contractCorpus,
      contracts,
      selectedContractorId,
      selectedContractId,
      onSessionCreated,
      toast,
    ],
  );

  // Run gate handler. When the active chat has content, offer to carry
  // it into the run (ADR-0017) via the run-options sheet; otherwise run
  // straight away. The Run button is gated on ``canRun`` so inputs are
  // already valid here.
  const onRun = useCallback(async () => {
    if (currentSessionId) {
      try {
        const session = await getChatSession(loadConfig(), currentSessionId);
        const msgs = session.messages.filter(
          (m) => m.role === "user" || m.role === "assistant",
        );
        if (msgs.length > 0) {
          setIncludeChat(true);
          setRunSheet({
            kind: "fresh",
            chatCount: msgs.length,
            transcript: buildChatTranscript(msgs),
          });
          return;
        }
      } catch {
        // A chat-fetch failure should never block a run; fall through
        // and run without carrying anything over.
      }
    }
    await doFreshRun("", "");
  }, [currentSessionId, doFreshRun]);

  // "Link to Assistant": re-point the Assistant at the selected
  // variation WITHOUT running a determination. This is the cheap
  // path the operator asked for: after uploading a new variation,
  // they can talk to the assistant about it (preliminary, retrieval
  // only) without paying for a full engine run. It re-points the
  // live session in place (or opens one if none is active); it does
  // NOT touch the determination output panel, which only ever shows
  // a completed run. assessment_id is cleared because no
  // determination exists yet for this variation, and the brief
  // carries contract_id so retrieval is scoped to the right corpus.
  const onLinkToAssistant = useCallback(async () => {
    if (!variationDoc) {
      toast.show("err", "Pick a variation submission first.");
      return;
    }
    const selectedContract = contracts.find((c) => c.id === selectedContractId);
    setLinking(true);
    const cfg = loadConfig();
    const variationName = variationDoc.original_filename;
    const brief: Record<string, unknown> = {
      // No assessment yet, so the brief's variation_id is the
      // operator-recognisable filename rather than a synthetic run id.
      variation_id: variationName,
      title: `Variation flow for ${variationName}`,
      documents: [
        {
          name: variationName,
          role: "variation submission",
          note: `Uploaded ${new Date(variationDoc.received_at).toLocaleString("en-AU")}.`,
        },
      ],
    };
    if (selectedContract) {
      // Retrieval scope (ADR-0015): contract entity id, matching how
      // documents are tagged on upload and how Tab-2 chats scope.
      brief.contract_id = selectedContract.id;
      brief.contract_reference = selectedContract.reference;
      if (selectedContract.name) brief.contract_name = selectedContract.name;
    }
    try {
      if (currentSessionId) {
        // assessment_id is sent explicitly (null) alongside the brief:
        // the backend requires the pair to move together when the
        // brief's variation identity changes, so retrieval scope and
        // any embedded determination can never disagree.
        await patchChatSession(cfg, currentSessionId, {
          variation_label: variationName,
          variation_brief: brief,
          assessment_id: null,
        });
        onAssistantSessionRelinked();
      } else {
        const created = await createChatSession(cfg, {
          kind: "workspace",
          title: variationName,
          variation_label: variationName,
          variation_brief: brief,
          assessment_id: null,
        });
        onSessionCreated(created.session_id);
      }
      // Clear the determination output so the right panel and the
      // Topbar do not keep advertising a prior variation's signed
      // determination while the Assistant is now scoped to this,
      // as-yet-undetermined, variation. The operator can still reopen
      // the prior chat to bring its determination back (the chat-
      // linked resume effect reloads it). Mirrors onReset's clear.
      sessionStorage.removeItem("cvp.ui.activeAssessment");
      setAssessment(null);
      setDetermination(null);
      setPhase("idle");
      toast.show(
        "ok",
        `Assistant linked to ${variationName} (preliminary; no determination run yet).`,
      );
    } catch (err) {
      const apiErr = err as ApiError;
      toast.show("err", `Link failed: ${apiErr.message ?? "unknown"}`);
    } finally {
      setLinking(false);
    }
  }, [
    variationDoc,
    contracts,
    selectedContractId,
    currentSessionId,
    onAssistantSessionRelinked,
    onSessionCreated,
    toast,
  ]);

  // Variations available for the current run gate are filtered by
  // the chosen contract: a variation is "available" iff its
  // document.contract_id matches the active contract. When no
  // contract is selected the list is empty so the dropdown reads
  // as "pick a contract first".
  const variationOptions = useMemo(
    () =>
      selectedContractId
        ? docs.filter(
            (d) =>
              classifyForRun(d.doc_type ?? "") === "variation"
              && d.contract_id === selectedContractId,
          )
        : [],
    [docs, selectedContractId],
  );
  // Contracts shown in the run-gate dropdown narrow to the chosen
  // contractor; without one the dropdown is disabled.
  const contractsForContractor = useMemo(
    () =>
      selectedContractorId
        ? contracts.filter((c) => c.contractor_id === selectedContractorId)
        : [],
    [contracts, selectedContractorId],
  );

  const onReset = useCallback(() => {
    sessionStorage.removeItem("cvp.ui.activeAssessment");
    setAssessment(null);
    setDetermination(null);
    setPhase("idle");
    toast.show("ok", "Cleared the active assessment.");
  }, [toast]);

  // Augment the current determination with new context (ADR-0018):
  // create a NEW assessment linked to the current one as its parent.
  // The backend reuses the parent's variation identity and source
  // documents, increments the revision, and carries the prior
  // determination forward as context. The conversation is preserved
  // (no fresh chat): the existing session is re-pointed at the new
  // revision in place.
  const doAugmentRun = useCallback(
    async (
      additionalContext: string,
      additionalContextLabel: string,
    ): Promise<void> => {
      if (
        !assessment
        || !assessment.base_contract_document_id
        || !assessment.submission_document_id
      ) {
        toast.show(
          "err",
          "This determination cannot be augmented; start a new run instead.",
        );
        return;
      }
      setRunning(true);
      const cfg = loadConfig();
      try {
        const created = await createAssessment(cfg, {
          // The backend re-derives identity from the parent; these
          // values satisfy request validation and match the parent.
          contract_id: assessment.contract_id,
          variation_id: assessment.variation_id,
          workflow_template:
            assessment.workflow_template as WorkflowTemplate,
          base_contract_document_id: assessment.base_contract_document_id,
          submission_document_id: assessment.submission_document_id,
          contractor_expected: "",
          parent_assessment_id: assessment.assessment_id,
          ...(additionalContext
            ? {
                additional_context: additionalContext,
                additional_context_label: additionalContextLabel,
              }
            : {}),
        });
        setAssessment(created);
        sessionStorage.setItem("cvp.ui.activeAssessment", JSON.stringify(created));
        setPhase(statusToPhase(created.status));
        setDetermination(null);
        // Preserve the conversation. Re-point the existing session at
        // the new revision so the binding chip tracks it. The variation
        // identity is unchanged, so a bare assessment_id patch is
        // allowed under ADR-0015 (no brief change).
        if (currentSessionId) {
          try {
            await patchChatSession(cfg, currentSessionId, {
              assessment_id: created.assessment_id,
            });
            onAssistantSessionRelinked();
          } catch {
            // Non-fatal: the run still proceeds; the chip may lag.
          }
        }
        toast.show(
          "ok",
          `Augmenting determination (revision ${created.revision ?? ""}).`,
        );
      } catch (err) {
        const apiErr = err as ApiError;
        toast.show("err", `Augment failed: ${apiErr.message ?? "unknown"}`);
      } finally {
        setRunning(false);
      }
    },
    [assessment, currentSessionId, onAssistantSessionRelinked, toast],
  );

  // Header "New run" handler. With a completed determination on file,
  // open the run-options sheet to choose new vs augment (and whether to
  // carry the chat in); otherwise just clear the panel.
  const onRerunClick = useCallback(async () => {
    const canAugment =
      phase === "complete"
      && !!determination
      && !!assessment
      && !!assessment.base_contract_document_id
      && !!assessment.submission_document_id;
    if (!canAugment) {
      onReset();
      return;
    }
    let chatCount = 0;
    let transcript = "";
    if (currentSessionId) {
      try {
        const session = await getChatSession(loadConfig(), currentSessionId);
        const msgs = session.messages.filter(
          (m) => m.role === "user" || m.role === "assistant",
        );
        chatCount = msgs.length;
        transcript = buildChatTranscript(msgs);
      } catch {
        // Ignore; the sheet just will not offer the chat option.
      }
    }
    setAugmentChoice("augment");
    setIncludeChat(chatCount > 0);
    setRunSheet({ kind: "rerun", chatCount, transcript });
  }, [phase, determination, assessment, currentSessionId, onReset]);

  // Confirm handler for the run-options sheet.
  const onConfirmRunSheet = useCallback(async () => {
    const sheet = runSheet;
    if (!sheet) return;
    const ctx = includeChat ? sheet.transcript : "";
    const label = includeChat ? chatContextLabel(sheet.chatCount) : "";
    setRunSheet(null);
    if (sheet.kind === "fresh") {
      await doFreshRun(ctx, label);
      return;
    }
    if (augmentChoice === "new") {
      onReset();
      return;
    }
    await doAugmentRun(ctx, label);
  }, [runSheet, includeChat, augmentChoice, doFreshRun, doAugmentRun, onReset]);

  return (
    <section className="col col-output col-variation" aria-label="Variation analysis">
      <header className="var-head">
        <div className="var-eyebrow">
          {determination
            ? `VARIATION · ${determination.determination.variation_id} · ${determination.determination.change_classification.toUpperCase()}`
            : "VARIATION ANALYSIS"}
        </div>
        <div className="var-eyebrow-r">
          {assessment && (assessment.revision ?? 1) > 1 && (
            <span
              className="rev-chip"
              title="This determination is an augmented revision of an earlier run"
            >
              Revision {assessment.revision}
            </span>
          )}
          <PhasePill phase={phase} />
          {(determination || assessment) && (
            <button
              className="btn btn-sm"
              type="button"
              onClick={() => void onRerunClick()}
              title="Run again: a fresh determination, or augment this one with new context"
              data-testid="new-run-button"
            >
              New run
            </button>
          )}
        </div>
      </header>

      <h1 className="var-title">
        {determination
          ? determination.determination.entitlement_finding.claim_summary
          : "No determination yet"}
      </h1>

      {determination ? (
        <DeterminationContent
          payload={determination}
          assessment={assessment}
          tab={tab}
          setTab={setTab}
          onDeterminationChange={setDetermination}
          onAssessmentChange={setAssessment}
        />
      ) : (
        <RunPanel
          docsLoading={docsLoading}
          variationOptions={variationOptions}
          contractCorpus={contractCorpus}
          variationDocId={variationDocId}
          setVariationDocId={setVariationDocId}
          contractors={contractors}
          contractsForContractor={contractsForContractor}
          selectedContractorId={selectedContractorId}
          setSelectedContractorId={setSelectedContractorId}
          selectedContractId={selectedContractId}
          setSelectedContractId={setSelectedContractId}
          phase={phase}
          assessment={assessment}
          running={running}
          linking={linking}
          onRun={onRun}
          onLinkToAssistant={onLinkToAssistant}
          onDeleteCorpusDocument={onDeleteCorpusDocument}
        />
      )}

      {runSheet && (
        <div
          className="sheet-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={
            runSheet.kind === "fresh" ? "Run determination" : "Run again"
          }
          onClick={() => !running && setRunSheet(null)}
        >
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sheet-head">
              <div className="sheet-title">
                {runSheet.kind === "fresh" ? "Run determination" : "Run again"}
              </div>
              <button
                className="btn-icon"
                type="button"
                aria-label="Close"
                onClick={() => setRunSheet(null)}
                disabled={running}
              >
                <Icon.cross />
              </button>
            </div>
            <div className="sheet-body">
              {runSheet.kind === "rerun" && (
                <fieldset
                  className="field"
                  style={{ border: "none", padding: 0, margin: 0 }}
                >
                  <legend
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      padding: 0,
                      marginBottom: 6,
                    }}
                  >
                    How should this run relate to the current determination?
                  </legend>
                  <label className="run-choice">
                    <input
                      type="radio"
                      name="augment-choice"
                      checked={augmentChoice === "augment"}
                      onChange={() => setAugmentChoice("augment")}
                      data-testid="augment-choice-augment"
                    />
                    <span>
                      <strong>Augment this determination</strong>: keep it on
                      file and create a new revision that carries it forward
                      with the new context.
                    </span>
                  </label>
                  <label className="run-choice">
                    <input
                      type="radio"
                      name="augment-choice"
                      checked={augmentChoice === "new"}
                      onChange={() => setAugmentChoice("new")}
                      data-testid="augment-choice-new"
                    />
                    <span>
                      <strong>New run</strong>: clear the panel and start a
                      fresh determination from scratch.
                    </span>
                  </label>
                </fieldset>
              )}
              {runSheet.chatCount > 0 && (
                <label className="run-choice" style={{ marginTop: 10 }}>
                  <input
                    type="checkbox"
                    checked={includeChat}
                    onChange={(e) => setIncludeChat(e.target.checked)}
                    data-testid="include-chat-checkbox"
                  />
                  <span>
                    Include the Assistant discussion ({runSheet.chatCount}{" "}
                    message{runSheet.chatCount === 1 ? "" : "s"}) as background.
                    It is treated as operator notes, not evidence; the
                    determination still cites only the documents.
                  </span>
                </label>
              )}
              <div className="actions" style={{ justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setRunSheet(null)}
                  disabled={running}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => void onConfirmRunSheet()}
                  disabled={running}
                  data-testid="run-confirm-button"
                >
                  {running
                    ? "Working…"
                    : runSheet.kind === "fresh"
                      ? "Run determination"
                      : augmentChoice === "augment"
                        ? "Augment"
                        : "New run"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function PhasePill({ phase }: { phase: Phase }) {
  if (phase === "running" || phase === "queued") {
    return (
      <span className="pill warn">
        <span className="pill-dot" />
        {phase === "queued" ? "Queued" : "Running"}
      </span>
    );
  }
  if (phase === "complete") {
    return (
      <span className="pill ok">
        <span className="pill-dot" />
        Determined
      </span>
    );
  }
  if (phase === "failed") {
    return (
      <span className="pill err">
        <span className="pill-dot" />
        Failed
      </span>
    );
  }
  return (
    <span className="pill warn">
      <span className="pill-dot" />
      Awaiting run
    </span>
  );
}

function RunPanel({
  docsLoading,
  variationOptions,
  contractCorpus,
  variationDocId,
  setVariationDocId,
  contractors,
  contractsForContractor,
  selectedContractorId,
  setSelectedContractorId,
  selectedContractId,
  setSelectedContractId,
  phase,
  assessment,
  running,
  linking,
  onRun,
  onLinkToAssistant,
  onDeleteCorpusDocument,
}: {
  docsLoading: boolean;
  variationOptions: DocumentRecord[];
  contractCorpus: DocumentRecord[];
  variationDocId: string;
  setVariationDocId: (id: string) => void;
  contractors: ContractorOut[];
  contractsForContractor: ContractOut[];
  selectedContractorId: string;
  setSelectedContractorId: (id: string) => void;
  selectedContractId: string;
  setSelectedContractId: (id: string) => void;
  phase: Phase;
  assessment: AssessmentRecord | null;
  running: boolean;
  linking: boolean;
  onRun: () => void;
  onLinkToAssistant: () => void;
  onDeleteCorpusDocument: (doc: DocumentRecord) => Promise<void>;
}) {
  const corpusReady = contractCorpus.length > 0;
  const canRun =
    !!selectedContractorId &&
    !!selectedContractId &&
    !!variationDocId &&
    corpusReady &&
    !running &&
    phase !== "running" &&
    phase !== "queued";
  // Linking is lighter-gated than running: it needs only a chosen
  // variation (a contract just sharpens retrieval scope). It does
  // not need the head-contract corpus that the determination
  // preflight requires, because no determination is run.
  const canLink = !!variationDocId && !linking && !running;
  return (
    <div className="analysis">
      <section className="vsection">
        <header className="vsection-head">
          <div className="vsection-icon">
            <Icon.sparkle />
          </div>
          <div className="vsection-title-block">
            <div className="vsection-title">Run the determination engine</div>
            <div className="vsection-sub">
              Preflight + agentic Claude loop + verifier
            </div>
          </div>
        </header>
        <div className="vsection-body">
          <p className="section-prose">
            Pick the contractor, the contract, and finally the variation
            submission to run against. The engine retrieves relevant
            clauses across the full contract corpus, drafts a
            determination with cited evidence, and surfaces the
            structured result here.
          </p>
          <div className="field">
            <label htmlFor="run-contractor">Contractor</label>
            <select
              id="run-contractor"
              value={selectedContractorId}
              onChange={(e) => setSelectedContractorId(e.target.value)}
              className="run-select"
            >
              <option value="">— Pick a contractor —</option>
              {contractors.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="run-contract">Contract</label>
            <select
              id="run-contract"
              value={selectedContractId}
              onChange={(e) => setSelectedContractId(e.target.value)}
              disabled={!selectedContractorId}
              className="run-select"
            >
              {!selectedContractorId ? (
                <option value="">Pick a contractor first…</option>
              ) : contractsForContractor.length === 0 ? (
                <option value="">
                  No contracts under this contractor yet — upload one on
                  the left
                </option>
              ) : (
                <>
                  <option value="">— Pick a contract —</option>
                  {contractsForContractor.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.reference}
                      {c.name ? ` — ${c.name}` : ""}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
          <div className="field">
            <label htmlFor="run-variation">Variation submission</label>
            <select
              id="run-variation"
              value={variationDocId}
              onChange={(e) => setVariationDocId(e.target.value)}
              disabled={!selectedContractId}
              className="run-select"
            >
              {!selectedContractId ? (
                <option value="">Pick a contract first…</option>
              ) : variationOptions.length === 0 ? (
                <option value="">
                  No variations attached to this contract yet — upload one
                  on the left
                </option>
              ) : (
                <>
                  <option value="">— Pick a variation —</option>
                  {variationOptions.map((d) => (
                    <option key={d.document_id} value={d.document_id}>
                      {d.original_filename}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
          <div className="corpus-summary">
            {corpusReady ? (
              <>
                <strong>{contractCorpus.length} contract document
                {contractCorpus.length === 1 ? "" : "s"}</strong> in scope:
                <ul>
                  {contractCorpus.slice(0, 6).map((d) => (
                    <li
                      key={d.document_id}
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <span style={{ flex: 1, minWidth: 0 }}>{d.original_filename}</span>
                      <button
                        type="button"
                        aria-label={`Remove ${d.original_filename} from the corpus`}
                        title={`Delete ${d.original_filename}`}
                        onClick={() => void onDeleteCorpusDocument(d)}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "var(--text-faint)",
                          cursor: "pointer",
                          padding: "0 4px",
                          fontSize: 13,
                          lineHeight: 1,
                        }}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                  {contractCorpus.length > 6 && (
                    <li className="more">…plus {contractCorpus.length - 6} more</li>
                  )}
                </ul>
              </>
            ) : (
              <span style={{ color: "var(--err)" }}>
                Upload at least one contract document on the left (toggle
                <strong> Contract</strong> on the dropzone) before running.
              </span>
            )}
          </div>
          <div className="actions">
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => void onLinkToAssistant()}
              disabled={!canLink}
              title={
                canLink
                  ? "Point the Assistant at this variation for a preliminary discussion, without running a determination"
                  : "Pick a variation submission first"
              }
              data-testid="link-to-assistant-button"
            >
              {linking ? "Linking…" : "Link to Assistant"}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={onRun}
              disabled={!canRun}
            >
              {running || phase === "queued" || phase === "running"
                ? "Running…"
                : "Run determination"}
            </button>
          </div>
          {docsLoading && (
            <p className="section-prose" style={{ color: "var(--text-muted)" }}>
              Loading uploaded documents…
            </p>
          )}
          {assessment && (phase === "running" || phase === "queued") && (
            <p className="section-prose" style={{ color: "var(--text-muted)" }}>
              <span className="spinner" />{" "}
              {assessment.phase_detail
                ? assessment.phase_detail
                : "Running assessment"}{" "}
              <span className="mono">
                ({assessment.assessment_id.slice(-8)})
              </span>
            </p>
          )}
          {assessment && phase === "failed" && (
            <p className="section-prose" style={{ color: "var(--err)" }}>
              The previous run failed:{" "}
              <span className="mono">{assessment.failure_reason ?? "unknown"}</span>.{" "}
              {isPlatformFault(assessment.failure_reason)
                ? "The platform's search index was unreachable, so the run stopped before any analysis. Your inputs are not the cause; ask the operator to check service health, then run again unchanged."
                : isTransientFailure(assessment.failure_reason)
                  ? "This looks transient (network or model timeout); try the run again, inputs do not need to change."
                  : "Adjust the inputs and run again."}
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

function DeterminationContent({
  payload,
  assessment,
  tab,
  setTab,
  onDeterminationChange,
  onAssessmentChange,
}: {
  payload: DeterminationPayload;
  assessment: AssessmentRecord | null;
  tab: Tab;
  setTab: (t: Tab) => void;
  onDeterminationChange: (d: DeterminationPayload) => void;
  onAssessmentChange: (a: AssessmentRecord) => void;
}) {
  const det = payload.determination;
  return (
    <>
      <div className="var-meta">
        <div>
          <div className="var-meta-l">CONTRACT</div>
          <div className="var-meta-v">{assessment?.contract_id ?? "-"}</div>
        </div>
        <div>
          <div className="var-meta-l">OUTCOME</div>
          <div className="var-meta-v">{det.determination_outcome.replace(/_/g, " ")}</div>
        </div>
        <div>
          <div className="var-meta-l">ASSESSED</div>
          <div className="var-meta-v">{formatMoney(deriveFinalAssessed(det))}</div>
        </div>
        <div>
          <div className="var-meta-l">CONFIDENCE</div>
          <div className="var-meta-v">
            {(payload.overall_confidence * 100).toFixed(0)}%
          </div>
        </div>
      </div>
      <div className="var-tabs">
        <button
          type="button"
          className={tab === "analysis" ? "on" : ""}
          onClick={() => setTab("analysis")}
        >
          Analysis
        </button>
        <button
          type="button"
          className={tab === "documents" ? "on" : ""}
          onClick={() => setTab("documents")}
        >
          Citations <span className="tab-meta">{payload.verdicts.length}</span>
        </button>
        <button
          type="button"
          className={tab === "history" ? "on" : ""}
          onClick={() => setTab("history")}
        >
          Telemetry
        </button>
      </div>
      {tab === "analysis" && (
        <AnalysisTab
          payload={payload}
          assessment={assessment}
          onDeterminationChange={onDeterminationChange}
          onAssessmentChange={onAssessmentChange}
        />
      )}
      {tab === "documents" && <CitationsTab payload={payload} />}
      {tab === "history" && <TelemetryTab payload={payload} assessment={assessment} />}
    </>
  );
}

type DeterminationOutcome =
  | "approved"
  | "partially_approved"
  | "rejected"
  | "no_variation"
  | "human_review_required";

const OUTCOME_OPTIONS: { value: DeterminationOutcome; label: string }[] = [
  { value: "approved", label: "Approved" },
  { value: "partially_approved", label: "Partially approved" },
  { value: "rejected", label: "Rejected" },
  { value: "no_variation", label: "No variation" },
  { value: "human_review_required", label: "Human review required" },
];

function AnalysisTab({
  payload,
  assessment,
  onDeterminationChange,
  onAssessmentChange,
}: {
  payload: DeterminationPayload;
  assessment: AssessmentRecord | null;
  onDeterminationChange: (d: DeterminationPayload) => void;
  onAssessmentChange: (a: AssessmentRecord) => void;
}) {
  const toast = useToast();
  const det = payload.determination;
  const issued = assessment?.issued_at != null;

  const [editing, setEditing] = useState(false);
  const [draftRecommendation, setDraftRecommendation] = useState(det.recommendation);
  const [draftOutcome, setDraftOutcome] = useState<DeterminationOutcome>(
    (det.determination_outcome as DeterminationOutcome) ?? "human_review_required",
  );
  const [draftFinal, setDraftFinal] = useState(det.final_assessed_value);
  const [draftAdjustment, setDraftAdjustment] = useState(det.contract_sum_adjustment);
  const [saving, setSaving] = useState(false);
  const approvalState = assessment?.approval_state ?? "draft";
  const isApproved = approvalState === "approved";
  const isPending = approvalState === "pending_approval";
  const changesRequested = approvalState === "changes_requested";

  // Re-sync the draft when the underlying determination changes
  // (e.g. after a successful save).
  useEffect(() => {
    setDraftRecommendation(det.recommendation);
    setDraftOutcome(
      (det.determination_outcome as DeterminationOutcome) ?? "human_review_required",
    );
    setDraftFinal(det.final_assessed_value);
    setDraftAdjustment(det.contract_sum_adjustment);
  }, [
    det.recommendation,
    det.determination_outcome,
    det.final_assessed_value,
    det.contract_sum_adjustment,
  ]);

  const onCancel = (): void => {
    setEditing(false);
    setDraftRecommendation(det.recommendation);
    setDraftOutcome(
      (det.determination_outcome as DeterminationOutcome) ?? "human_review_required",
    );
    setDraftFinal(det.final_assessed_value);
    setDraftAdjustment(det.contract_sum_adjustment);
  };

  const onSave = async (): Promise<void> => {
    if (!assessment) return;
    const body: DeterminationEdit = {};
    if (draftRecommendation.trim() !== det.recommendation) {
      body.recommendation = draftRecommendation.trim();
    }
    if (draftOutcome !== det.determination_outcome) {
      body.determination_outcome = draftOutcome;
    }
    if (draftFinal !== det.final_assessed_value) {
      body.final_assessed_value = draftFinal;
    }
    if (draftAdjustment !== det.contract_sum_adjustment) {
      body.contract_sum_adjustment = draftAdjustment;
    }
    if (Object.keys(body).length === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const cfg = loadConfig();
      const updated = await patchDetermination(cfg, assessment.assessment_id, body);
      onDeterminationChange(updated);
      setEditing(false);
      toast.show("ok", "Determination updated.");
    } catch (err) {
      const apiErr = err as ApiError;
      toast.show("err", `Save failed: ${apiErr.message ?? "unknown"}`);
    } finally {
      setSaving(false);
    }
  };

  // Two-stage approval workflow. The "Issue determination" button
  // is replaced by "Send for approval" → reviewer → approve (which
  // implicitly issues) or request changes (back to operator). The
  // approver email is captured the first time and persisted to
  // the contract row so the dialog pre-fills on subsequent sends.
  const [sendOpen, setSendOpen] = useState(false);
  const [approverEmail, setApproverEmail] = useState("");
  const [sendNote, setSendNote] = useState("");
  const [sending, setSending] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [changesComment, setChangesComment] = useState("");
  const [requestingChanges, setRequestingChanges] = useState(false);
  const [approving, setApproving] = useState(false);

  // Pre-fill the approver email. The assessment carries the most
  // recent value; if it's empty we fall back to the contract row's
  // approver_email (set by an earlier assessment on the same
  // contract) so the operator does not retype across variations.
  useEffect(() => {
    if (!assessment || sendOpen === false) return;
    if (approverEmail) return;
    const cfg = loadConfig();
    const fallback = assessment.approver_email ?? "";
    if (fallback) {
      setApproverEmail(fallback);
      return;
    }
    void listContracts(cfg).then((cs) => {
      const match = cs.find(
        (c) => c.reference === assessment.contract_id,
      );
      // ContractOut here is from openapi.gen and may not yet
      // expose approver_email; use a guarded property read so the
      // fallback degrades gracefully on older schema responses.
      const email = (match as ContractOut & { approver_email?: string | null })
        ?.approver_email;
      if (email) setApproverEmail(email);
    });
  }, [assessment, sendOpen, approverEmail]);

  const onSendForApproval = async (): Promise<void> => {
    if (!assessment) return;
    if (!approverEmail.trim()) {
      toast.show("err", "Approver email is required.");
      return;
    }
    setSending(true);
    try {
      const cfg = loadConfig();
      const result = await sendForApproval(cfg, assessment.assessment_id, {
        approver_email: approverEmail.trim(),
        note: sendNote.trim(),
      });
      onAssessmentChange(result.record);
      toast.show("ok", `Sent for approval to ${approverEmail.trim()}.`);
      setSendOpen(false);
      setSendNote("");
    } catch (err) {
      const apiErr = err as ApiError;
      toast.show("err", `Send failed: ${apiErr.message ?? "unknown"}`);
    } finally {
      setSending(false);
    }
  };

  const onApprove = async (): Promise<void> => {
    if (!assessment) return;
    const approvedBy = window.prompt(
      "Approving the determination issues it: PDFs are re-rendered with your name in the signature block, editing is locked, and an audit record is written. Type your name to confirm:",
      "Dr Troy Neilson",
    );
    if (approvedBy === null) return;
    const trimmed = approvedBy.trim();
    if (trimmed.length === 0) return;
    setApproving(true);
    try {
      const cfg = loadConfig();
      const result = await approveDetermination(
        cfg,
        assessment.assessment_id,
        trimmed,
      );
      onAssessmentChange(result.record);
      toast.show("ok", `Approved by ${trimmed}.`);
    } catch (err) {
      const apiErr = err as ApiError;
      toast.show("err", `Approve failed: ${apiErr.message ?? "unknown"}`);
    } finally {
      setApproving(false);
    }
  };

  const onRequestChanges = async (): Promise<void> => {
    if (!assessment) return;
    if (changesComment.trim().length === 0) {
      toast.show("err", "Add a comment so the operator knows what to amend.");
      return;
    }
    setRequestingChanges(true);
    try {
      const cfg = loadConfig();
      const result = await requestChangesOnDetermination(
        cfg,
        assessment.assessment_id,
        changesComment.trim(),
      );
      onAssessmentChange(result.record);
      toast.show("ok", "Sent back for amendment.");
      setChangesOpen(false);
      setChangesComment("");
    } catch (err) {
      const apiErr = err as ApiError;
      toast.show("err", `Request changes failed: ${apiErr.message ?? "unknown"}`);
    } finally {
      setRequestingChanges(false);
    }
  };

  const [deliverOpen, setDeliverOpen] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const [deliverEmail, setDeliverEmail] = useState("");
  const [deliverContractor, setDeliverContractor] = useState("");
  const [deliverNote, setDeliverNote] = useState("");
  const [deliveredAt, setDeliveredAt] = useState<string | null>(null);
  const [deliveredTo, setDeliveredTo] = useState<string | null>(null);
  // Briefing-note generation dialog. The dialog captures the
  // operator-supplied metadata and Yes/No judgements that the
  // determination engine cannot answer; submitting produces and
  // persists the .docx so the existing Email determination flow
  // can attach the populated document (ADR-0008).
  const [briefingDialogOpen, setBriefingDialogOpen] = useState(false);
  // Determination PDF download. The artefact is rendered by the
  // engine on every save; this just streams it down so the
  // operator can keep a local copy without going through the
  // email-determination flow.
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const onDownloadDeterminationPdf = async (): Promise<void> => {
    if (!assessment) return;
    setDownloadingPdf(true);
    try {
      const cfg = loadConfig();
      const blob = await fetchArtifact(
        cfg, assessment.assessment_id, "determination-letter",
      );
      if (blob === null) {
        toast.show(
          "err",
          "The determination PDF is not ready yet. Save or re-run the assessment and try again.",
        );
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `determination-${assessment.variation_id || assessment.assessment_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const apiErr = err as ApiError;
      toast.show("err", `Download failed: ${apiErr.message ?? "unknown"}`);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const onDeliver = async (): Promise<void> => {
    if (!assessment) return;
    if (!deliverEmail.trim()) {
      toast.show("err", "Enter the contractor's email address.");
      return;
    }
    setDelivering(true);
    try {
      const cfg = loadConfig();
      const result = await deliverDetermination(cfg, assessment.assessment_id, {
        contractor_email: deliverEmail.trim(),
        contractor_name: deliverContractor.trim(),
        note: deliverNote.trim(),
      });
      setDeliveredAt(result.delivered_at);
      setDeliveredTo(result.contractor_email);
      setDeliverOpen(false);
      setDeliverNote("");
      toast.show(
        "ok",
        `Sent to ${result.contractor_email}. Delivery ${result.delivery_record_id}.`,
      );
    } catch (err) {
      const apiErr = err as ApiError;
      toast.show("err", `Send failed: ${apiErr.message ?? "unknown"}`);
    } finally {
      setDelivering(false);
    }
  };

  return (
    <div className="analysis">
      <div className={`decision-banner ${issued ? "issued" : ""}`}>
        <div className="decision-banner-icon">
          {issued ? <Icon.check /> : <Icon.decision />}
        </div>
        <div className="decision-banner-body">
          <div className="decision-banner-eyebrow">
            {issued ? "ISSUED DETERMINATION" : "RECOMMENDED DECISION"}
          </div>
          {editing ? (
            <>
              <select
                className="banner-outcome-input"
                value={draftOutcome}
                onChange={(e) => setDraftOutcome(e.target.value as DeterminationOutcome)}
              >
                {OUTCOME_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <textarea
                className="banner-recommendation-input"
                rows={4}
                value={draftRecommendation}
                onChange={(e) => setDraftRecommendation(e.target.value)}
              />
              <div className="banner-edit-row">
                <label>
                  <span>Final assessed (A$)</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={draftFinal}
                    onChange={(e) => setDraftFinal(e.target.value)}
                  />
                </label>
                <label>
                  <span>Contract sum adjustment (A$)</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={draftAdjustment}
                    onChange={(e) => setDraftAdjustment(e.target.value)}
                  />
                </label>
              </div>
            </>
          ) : (
            <>
              <div className="decision-banner-title">
                {det.determination_outcome.replace(/_/g, " ")}
              </div>
              <div className="decision-banner-sub">{det.entitlement_finding.basis_clause}</div>
              <div className="decision-banner-detail">{det.recommendation}</div>
              {issued && assessment?.issued_at && (
                <div className="banner-issued-meta">
                  Issued by <strong>{assessment.issued_by}</strong>
                  {" on "}
                  {new Date(assessment.issued_at).toLocaleString("en-AU", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </div>
              )}
            </>
          )}
        </div>
        <div className="decision-banner-actions">
          {editing ? (
            <>
              <button
                className="btn btn-sm"
                type="button"
                onClick={onCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-sm"
                type="button"
                onClick={() => void onSave()}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </>
          ) : (
            <>
              <button
                className="btn btn-sm"
                type="button"
                onClick={() => setEditing(true)}
                disabled={issued || isPending}
                title={
                  issued
                    ? "Approved determinations are locked"
                    : isPending
                      ? "Wait for the approver before editing"
                      : "Edit the determination"
                }
              >
                Edit
              </button>
              {!isPending && !isApproved && (
                <button
                  className="btn btn-primary btn-sm"
                  type="button"
                  onClick={() => setSendOpen(true)}
                  disabled={sending}
                  title={
                    changesRequested
                      ? "Resend the amended determination for approval"
                      : "Send the determination to the approver for review"
                  }
                >
                  {sending
                    ? "Sending…"
                    : changesRequested
                      ? "Resend for approval"
                      : "Send for approval"}
                </button>
              )}
              <button
                className="btn btn-sm"
                type="button"
                onClick={() => void onDownloadDeterminationPdf()}
                disabled={downloadingPdf || !assessment}
                title="Download the determination output as a PDF"
                data-testid="download-determination-pdf-button"
              >
                {downloadingPdf ? "Downloading…" : "Download PDF"}
              </button>
              {isPending && (
                <>
                  <button
                    className="btn btn-primary btn-sm"
                    type="button"
                    onClick={() => void onApprove()}
                    disabled={approving}
                    title="Approve and issue the determination"
                  >
                    {approving ? "Approving…" : "Approve"}
                  </button>
                  <button
                    className="btn btn-sm"
                    type="button"
                    onClick={() => setChangesOpen(true)}
                    disabled={requestingChanges}
                    title="Send back for amendment"
                  >
                    Request changes
                  </button>
                </>
              )}
              {isApproved && (
                // Rendered as a disabled button (rather than a pill)
                // so it picks up the full ``.btn .btn-sm`` chrome
                // (height, padding, font, border, shadow, gradient
                // base) and lines up pixel-for-pixel with the
                // adjacent Edit / Generate / Email controls. The
                // green tint is applied inline so the status reads
                // visually distinct from a normal action, while
                // ``disabled`` + ``aria-disabled`` signal that the
                // chip is a state indicator, not a clickable
                // control.
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled
                  aria-disabled="true"
                  style={{
                    background: "rgba(48, 209, 88, 0.18)",
                    color: "#1a8b3d",
                    borderColor: "rgba(48, 160, 88, 0.35)",
                    cursor: "default",
                    opacity: 1,
                  }}
                >
                  Approved
                </button>
              )}
              <button
                className="btn btn-sm"
                type="button"
                onClick={() => setBriefingDialogOpen(true)}
                disabled={!issued || delivering}
                title={
                  issued
                    ? "Fill the operator questions and generate the briefing note Word document"
                    : "Issue the determination before generating the briefing note"
                }
                data-testid="generate-briefing-note-button"
              >
                Generate briefing note
              </button>
              <button
                className={`btn btn-sm ${isApproved ? "btn-primary" : ""}`}
                type="button"
                onClick={() => setDeliverOpen(true)}
                disabled={!isApproved || delivering}
                title={
                  isApproved
                    ? "Send the approved determination by email"
                    : "Approve the determination before emailing it"
                }
              >
                {deliveredTo ? "Resend determination" : "Email determination"}
              </button>
            </>
          )}
        </div>
      </div>

      {deliveredTo && (
        <div className="delivery-strip">
          Delivered to <strong>{deliveredTo}</strong>
          {deliveredAt && (
            <>
              {" on "}
              {new Date(deliveredAt).toLocaleString("en-AU", {
                dateStyle: "medium",
                timeStyle: "short",
              })}
            </>
          )}
        </div>
      )}

      {isPending && assessment && (
        <div className="approval-banner approval-banner-pending">
          <strong>Pending approval</strong> — sent to{" "}
          <strong>{assessment.approver_email ?? "—"}</strong>
          {assessment.approval_sent_at && (
            <>
              {" on "}
              {new Date(assessment.approval_sent_at).toLocaleString("en-AU", {
                dateStyle: "medium",
                timeStyle: "short",
              })}
            </>
          )}
          .
        </div>
      )}

      {changesRequested && assessment?.approval_comment && (
        <div className="approval-banner approval-banner-changes">
          <strong>Changes requested:</strong> {assessment.approval_comment}
        </div>
      )}

      {sendOpen && (
        <div
          className="sheet-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Send for approval"
          onClick={() => !sending && setSendOpen(false)}
        >
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sheet-head">
              <div className="sheet-title">Send for approval</div>
              <button
                className="btn-icon"
                type="button"
                aria-label="Close"
                onClick={() => setSendOpen(false)}
                disabled={sending}
              >
                <Icon.cross />
              </button>
            </div>
            <div className="sheet-body">
              <p style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 0 }}>
                Emails the approver a notification and moves the
                determination to <strong>pending approval</strong>. The
                email persists per contract; subsequent variations on
                the same contract pre-fill it for you.
              </p>
              <div className="field">
                <label htmlFor="snd-email">Approver email</label>
                <input
                  id="snd-email"
                  type="email"
                  value={approverEmail}
                  onChange={(e) => setApproverEmail(e.target.value)}
                  placeholder="approver@example.com"
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="snd-note">Note (optional)</label>
                <textarea
                  id="snd-note"
                  rows={3}
                  value={sendNote}
                  onChange={(e) => setSendNote(e.target.value)}
                  placeholder="Anything the approver should know."
                  style={{
                    font: "inherit",
                    fontSize: 13,
                    width: "100%",
                    padding: 8,
                    border: "0.5px solid var(--border-strong)",
                    borderRadius: 6,
                    resize: "vertical",
                  }}
                />
              </div>
              <div className="actions" style={{ justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setSendOpen(false)}
                  disabled={sending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => void onSendForApproval()}
                  disabled={sending || !approverEmail.trim()}
                >
                  {sending ? "Sending…" : "Send"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {changesOpen && (
        <div
          className="sheet-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Request changes"
          onClick={() => !requestingChanges && setChangesOpen(false)}
        >
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sheet-head">
              <div className="sheet-title">Request changes</div>
              <button
                className="btn-icon"
                type="button"
                aria-label="Close"
                onClick={() => setChangesOpen(false)}
                disabled={requestingChanges}
              >
                <Icon.cross />
              </button>
            </div>
            <div className="sheet-body">
              <p style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 0 }}>
                Sends the determination back for amendment. The
                operator sees the comment inline; once amended they
                send it back for approval.
              </p>
              <div className="field">
                <label htmlFor="rc-comment">Comment</label>
                <textarea
                  id="rc-comment"
                  rows={5}
                  value={changesComment}
                  onChange={(e) => setChangesComment(e.target.value)}
                  placeholder="Describe what needs to change."
                  style={{
                    font: "inherit",
                    fontSize: 13,
                    width: "100%",
                    padding: 8,
                    border: "0.5px solid var(--border-strong)",
                    borderRadius: 6,
                    resize: "vertical",
                  }}
                />
              </div>
              <div className="actions" style={{ justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setChangesOpen(false)}
                  disabled={requestingChanges}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => void onRequestChanges()}
                  disabled={requestingChanges || !changesComment.trim()}
                >
                  {requestingChanges ? "Sending…" : "Send back"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deliverOpen && (
        <div
          className="sheet-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Email determination"
          onClick={() => !delivering && setDeliverOpen(false)}
        >
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sheet-head">
              <div className="sheet-title">Email determination</div>
              <button
                className="btn-icon"
                type="button"
                aria-label="Close"
                onClick={() => setDeliverOpen(false)}
                disabled={delivering}
              >
                <Icon.cross />
              </button>
            </div>
            <div className="sheet-body">
              <p style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 0 }}>
                Sends the issued determination by email and writes a
                delivery record into the audit trail.
              </p>
              <div className="field">
                <label htmlFor="dlv-email">Recipient email</label>
                <input
                  id="dlv-email"
                  type="email"
                  value={deliverEmail}
                  onChange={(e) => setDeliverEmail(e.target.value)}
                  placeholder="recipient@example.com"
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="dlv-name">Recipient name (optional)</label>
                <input
                  id="dlv-name"
                  type="text"
                  value={deliverContractor}
                  onChange={(e) => setDeliverContractor(e.target.value)}
                  placeholder="Acme Construction Pty Ltd"
                />
              </div>
              <div className="field">
                <label htmlFor="dlv-note">Cover note (optional)</label>
                <textarea
                  id="dlv-note"
                  rows={4}
                  value={deliverNote}
                  onChange={(e) => setDeliverNote(e.target.value)}
                  placeholder="Optional cover note included with the determination."
                  style={{
                    font: "inherit",
                    fontSize: 13,
                    width: "100%",
                    padding: 8,
                    border: "0.5px solid var(--border-strong)",
                    borderRadius: 6,
                    resize: "vertical",
                  }}
                />
              </div>
              <div className="actions" style={{ justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setDeliverOpen(false)}
                  disabled={delivering}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => void onDeliver()}
                  disabled={delivering || !deliverEmail.trim()}
                >
                  {delivering ? "Sending…" : "Send"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {assessment && (
        <BriefingNoteDialog
          assessmentId={assessment.assessment_id}
          variationId={assessment.variation_id}
          open={briefingDialogOpen}
          onClose={() => setBriefingDialogOpen(false)}
        />
      )}

      <Section title="Variation summary" icon={<Icon.doc />}>
        <p className="section-prose">{det.entitlement_finding.claim_summary}</p>
        <div className="defs">
          <div className="def-l">Determination ID</div>
          <div className="def-v mono" style={{ wordBreak: "break-all" }}>
            {det.determination_id}
          </div>
          <div className="def-l">Classification</div>
          <div className="def-v">{det.change_classification.replace(/_/g, " ")}</div>
          <div className="def-l">Outcome</div>
          <div className="def-v">{det.determination_outcome.replace(/_/g, " ")}</div>
          <div className="def-l">Final assessed</div>
          <div className="def-v">{formatMoney(deriveFinalAssessed(det))}</div>
          <div className="def-l">Adjustment</div>
          <div className="def-v">{formatMoney(deriveAdjustment(det))}</div>
        </div>
      </Section>

      <Section
        title="Entitlement finding"
        subtitle={det.entitlement_finding.basis_clause}
        icon={<Icon.shield />}
        rightPill={
          <span
            className={`section-pill ${
              det.entitlement_finding.outcome === "approved"
                ? "ok"
                : det.entitlement_finding.outcome === "rejected"
                  ? "err"
                  : "warn"
            }`}
          >
            <span className="pill-dot" />
            {det.entitlement_finding.outcome.replace(/_/g, " ")}
          </span>
        }
      >
        <p className="section-prose">{det.entitlement_finding.reasoning}</p>
        {det.entitlement_finding.citations.length > 0 && (
          <>
            <div className="cited-eyebrow">CITED EVIDENCE</div>
            <div className="cited">
              {det.entitlement_finding.citations.map((c) => (
                <div key={c.chunk_id} className="cited-row">
                  <div className="cited-cite">
                    {c.document_id} · p. {c.page} · {c.chunk_id.slice(0, 12)}
                  </div>
                  <div className="cited-quote">{c.excerpt}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </Section>

      {det.valuation_lines.length > 0 && (
        <Section
          title="Cost & schedule impact"
          subtitle={`${det.valuation_lines.length} line item${det.valuation_lines.length === 1 ? "" : "s"}`}
          icon={<Icon.ledger />}
        >
          <table className="cost-table">
            <thead>
              <tr>
                <th>LINE ITEM</th>
                <th>METHOD</th>
                <th>CLAIMED</th>
                <th>ASSESSED</th>
                <th>Δ</th>
              </tr>
            </thead>
            <tbody>
              {det.valuation_lines.map((line, i) => {
                const claimed = Number(line.claimed_amount);
                const assessed = Number(line.assessed_amount);
                const delta = assessed - claimed;
                return (
                  <tr key={i}>
                    <td>
                      <div className="cost-item">{line.cost_element}</div>
                      {line.notes && <div className="cost-detail">{line.notes}</div>}
                      <div className="cost-detail">{line.contract_basis}</div>
                    </td>
                    <td>
                      <span className="mono" style={{ fontSize: 11 }}>
                        {line.methodology.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="num">{formatMoney(line.claimed_amount)}</td>
                    <td className="num">{formatMoney(line.assessed_amount)}</td>
                    <td className="num delta">
                      {delta === 0 ? "-" : formatMoney(String(delta))}
                    </td>
                  </tr>
                );
              })}
              <tr className="cost-total">
                <td>Net effect on Contract Sum</td>
                <td></td>
                <td className="num">-</td>
                <td className="num ok">
                  {formatMoney(deriveAdjustment(det))}
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </Section>
      )}

      <Section
        title="Time impact"
        subtitle={det.time_impact.time_clause}
        icon={<Icon.clock />}
        rightPill={
          <span className={`section-pill ${det.time_impact.entitled ? "ok" : "warn"}`}>
            <span className="pill-dot" />
            {det.time_impact.entitled ? "Entitled" : "Not entitled"}
          </span>
        }
      >
        <div className="defs">
          <div className="def-l">EOT claimed</div>
          <div className="def-v">{det.time_impact.eot_claimed ? "Yes" : "No"}</div>
          <div className="def-l">Critical path</div>
          <div className="def-v">{det.time_impact.critical_path ? "Yes" : "No"}</div>
          <div className="def-l">Assessed delay</div>
          <div className="def-v">{det.time_impact.assessed_delay_days} days</div>
          <div className="def-l">Concurrent delay</div>
          <div className="def-v">{det.time_impact.concurrent_delay ? "Yes" : "No"}</div>
        </div>
      </Section>

      {det.non_compliance.length > 0 && (
        <Section
          title="Non-compliance"
          subtitle={`${det.non_compliance.length} item${det.non_compliance.length === 1 ? "" : "s"}`}
          icon={<Icon.cross />}
          rightPill={
            <span className="section-pill err">
              <span className="pill-dot" />
              Action required
            </span>
          }
        >
          <ul className="check-list">
            {det.non_compliance.map((nc, i) => (
              <li key={i} className="check err">
                <span className="check-icon">
                  <Icon.cross />
                </span>
                <div>
                  <div className="check-title">{nc.category.replace(/_/g, " ")}</div>
                  <div className="check-detail">{nc.description}</div>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

function CitationsTab({ payload }: { payload: DeterminationPayload }) {
  return (
    <div className="analysis">
      <Section title="All cited assertions" icon={<Icon.doc />}>
        <ul className="check-list">
          {payload.verdicts.map((v) => {
            const ok = v.combined === "supported";
            return (
              <li key={`${v.field_label}-${v.chunk_id}`} className={`check ${ok ? "ok" : "err"}`}>
                <span className="check-icon">
                  {ok ? <Icon.check /> : <Icon.cross />}
                </span>
                <div>
                  <div className="check-title">{v.field_label}</div>
                  <div className="check-detail mono" style={{ fontSize: 11 }}>
                    chunk {v.chunk_id.slice(0, 14)} · det {v.deterministic} ·
                    judge {v.judge} · combined {v.combined} ·
                    sim {v.det_similarity.toFixed(2)}
                  </div>
                  {v.judge_rationale && (
                    <div className="check-detail">{v.judge_rationale}</div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </Section>
    </div>
  );
}

function TelemetryTab({
  payload,
  assessment,
}: {
  payload: DeterminationPayload;
  assessment: AssessmentRecord | null;
}) {
  const t = payload.determination.telemetry;
  return (
    <div className="analysis">
      <Section title="Run telemetry" icon={<Icon.history />}>
        <div className="defs">
          <div className="def-l">Assessment id</div>
          <div className="def-v mono">{assessment?.assessment_id ?? "-"}</div>
          <div className="def-l">Determination id</div>
          <div className="def-v mono">{payload.determination.determination_id}</div>
          <div className="def-l">Model</div>
          <div className="def-v">{t.model}</div>
          <div className="def-l">Input tokens</div>
          <div className="def-v">{t.input_tokens.toLocaleString("en-AU")}</div>
          <div className="def-l">Output tokens</div>
          <div className="def-v">{t.output_tokens.toLocaleString("en-AU")}</div>
          <div className="def-l">Tool calls</div>
          <div className="def-v">{t.tool_calls}</div>
          <div className="def-l">Elapsed</div>
          <div className="def-v">{(t.elapsed_ms / 1000).toFixed(1)} s</div>
          <div className="def-l">Cost</div>
          <div className="def-v">USD {t.cost_usd}</div>
          <div className="def-l">Verifier confidence</div>
          <div className="def-v">{(payload.overall_confidence * 100).toFixed(0)}%</div>
          <div className="def-l">Retries consumed</div>
          <div className="def-v">{payload.retries_consumed}</div>
          <div className="def-l">Signature</div>
          <div className="def-v mono" style={{ wordBreak: "break-all" }}>
            {payload.signature.slice(0, 32)}…
          </div>
        </div>
      </Section>
    </div>
  );
}

function Section({
  title,
  subtitle,
  icon,
  rightPill,
  children,
}: {
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  rightPill?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="vsection">
      <header className="vsection-head">
        <div className="vsection-icon">{icon}</div>
        <div className="vsection-title-block">
          <div className="vsection-title">{title}</div>
          {subtitle && <div className="vsection-sub">{subtitle}</div>}
        </div>
        <div className="vsection-right">
          {rightPill}
        </div>
      </header>
      <div className="vsection-body">{children}</div>
    </section>
  );
}
